import { useNavigate } from "react-router-dom";
import type { Plan, PlanStage } from "../domain/models";
import { t } from "../i18n";
import { Button, Icon, Tag } from "./ui";

export function PlanCard({
  plan,
  onSelect,
}: {
  plan: Plan;
  onSelect: () => void;
}) {
  const cover = plan.stages.find((stage) => stage.image)?.image;
  return (
    <article className="plan-card">
      <div
        className="plan-card__cover"
        style={{
          backgroundImage: `linear-gradient(180deg,rgba(18,67,62,.04),rgba(16,50,47,.86)),url(${cover ?? ""})`,
        }}
      >
        <div className="row row--between">
          <Tag tone="accent">PLAN {plan.alternative}</Tag>
          {plan.featuredLabel && <Tag tone="warning">{plan.featuredLabel}</Tag>}
        </div>
        <div>
          <h2>{plan.reason}</h2>
          <p>
            {plan.stages.length} {t.plans.stops} ·{" "}
            {durationBetween(plan.startsAt, plan.endsAt)}
          </p>
        </div>
      </div>
      <PlanMetrics plan={plan} />
      <WeatherLine plan={plan} />
      <div className="timeline">
        {plan.stages.map((stage) => (
          <StageRow key={stage.id} stage={stage} />
        ))}
      </div>
      <Button onClick={onSelect}>View full plan</Button>
    </article>
  );
}

export function PlanMetrics({ plan }: { plan: Plan }) {
  return (
    <div className="plan-metrics">
      <div>
        <small>{t.plans.duration}</small>
        <strong>{durationBetween(plan.startsAt, plan.endsAt)}</strong>
      </div>
      <div>
        <small>{t.plans.cost}</small>
        <strong>{money(plan.estimatedPerPersonCostMinor)}</strong>
      </div>
      <div>
        <small>{t.plans.driving}</small>
        <strong>{minutes(plan.totalRouteDurationSeconds)} min</strong>
      </div>
    </div>
  );
}
export function WeatherLine({ plan }: { plan: Plan }) {
  if (plan.weather.availability !== "available")
    return (
      <p className="weather-line weather-line--quiet">
        <span aria-hidden>◌</span>
        {t.plans.weatherUnavailable}
      </p>
    );
  return (
    <p className="weather-line">
      <span aria-hidden>
        {plan.weather.precipitationProbability !== null &&
        plan.weather.precipitationProbability > 0.5
          ? "☂"
          : "☀"}
      </span>
      {plan.weather.condition} · Planning context, not a guarantee
    </p>
  );
}
export function StageRow({
  stage,
  actions,
}: {
  stage: PlanStage;
  actions?: React.ReactNode;
}) {
  const travel = stage.route
    ? `${Math.ceil(stage.route.durationSeconds / 60)} min`
    : null;
  return (
    <div className={`stage-row ${stage.isRedacted ? "stage-row--locked" : ""}`}>
      <div className="stage-row__time">
        <strong>{time(stage.startsAt)}</strong>
        {travel && (
          <small>
            <Icon name="car" /> {travel}
          </small>
        )}
      </div>
      <div className="timeline__line">
        <span>{stage.stage}</span>
      </div>
      <div className="stage-row__content">
        {stage.isRedacted ? (
          <>
            <div className="locked-content" aria-hidden>
              <h3>Hidden activity</h3>
              <p>Matched to your time and interests</p>
            </div>
            <div className="lock-overlay">
              <Icon name="lock" />
              <strong>Sign in to unlock</strong>
            </div>
          </>
        ) : (
          <>
            <small>{label(stage.category)}</small>
            <h3>{stage.offeringName}</h3>
            <p>
              {stage.venueName}
              {stage.branchName ? ` · ${stage.branchName}` : ""}
            </p>
            <div className="stage-facts">
              <span>
                <Icon name="clock" />{" "}
                {durationBetween(stage.startsAt, stage.endsAt)}
              </span>
              {stage.route && (
                <span>
                  <Icon name="car" />{" "}
                  {stage.stage === 1
                    ? t.plans.fromOrigin
                    : t.plans.fromPrevious}
                  : {travel} ({t.plans.estimated})
                </span>
              )}
              <span>{money(stage.estimatedPerPersonCostMinor)}</span>
            </div>
            <div className="row row--wrap">
              <Tag tone={stage.reservationRequired ? "warning" : "success"}>
                {t.booking[stage.bookingMode as keyof typeof t.booking]}
              </Tag>
              {stage.weatherDecision !== "unknown" && (
                <Tag
                  tone={
                    stage.weatherDecision === "caution" ? "warning" : "neutral"
                  }
                >
                  {t.weather[stage.weatherDecision as "suitable" | "caution"]}
                </Tag>
              )}
              {stage.minimumAge !== undefined && stage.minimumAge > 0 && (
                <Tag tone="warning">{stage.minimumAge}+ only</Tag>
              )}
              {stage.featuredLabel && (
                <Tag tone="warning">{stage.featuredLabel}</Tag>
              )}
            </div>
            {actions}
          </>
        )}
      </div>
    </div>
  );
}
const time = (value: string) =>
  new Intl.DateTimeFormat("en-LB", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Beirut",
  }).format(new Date(value));
const durationBetween = (start: string, end: string) => {
  const total = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000),
  );
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return hours ? `${hours}h${mins ? ` ${mins}m` : ""}` : `${mins}m`;
};
const minutes = (seconds: number) => Math.ceil(seconds / 60);
const money = (minor: number) =>
  minor === 0
    ? "Free"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(minor / 100);
const label = (value: string) =>
  value
    .split("_")
    .map((word) => (word[0] ?? "").toUpperCase() + word.slice(1))
    .join(" ");
export function PlanCardLink({ plan }: { plan: Plan }) {
  const navigate = useNavigate();
  return (
    <PlanCard plan={plan} onSelect={() => navigate(`/plans/${plan.id}`)} />
  );
}
